import { useState, useEffect } from 'react';
import { AsYouType, parsePhoneNumberFromString, CountryCode } from 'libphonenumber-js';
import clsx from 'clsx';
import { CountrySelect } from './CountrySelect';
import { useCompanyCountry } from '@/contexts/WorkspaceContext';

interface Props {
  value?: string;
  defaultCountry?: CountryCode;
  onChange: (value: { e164: string; country: CountryCode | null; isValid: boolean }) => void;
  placeholder?: string;
  className?: string;
}

export const PhoneInput: React.FC<Props> = ({ value, defaultCountry, onChange, placeholder, className }) => {
  const companyCountry = useCompanyCountry() as CountryCode;
  const fallbackCountry = defaultCountry ?? companyCountry;
  const [country, setCountry] = useState<CountryCode>(fallbackCountry);
  const [raw, setRaw] = useState<string>(value ?? '');

  // Follow the company's country once the workspace answers, until the user has typed a number.
  useEffect(() => {
    if (!raw) setCountry(fallbackCountry);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fallbackCountry]);

  useEffect(() => {
    if (!value) return;
    const parsed = parsePhoneNumberFromString(value);
    if (parsed?.country) { setCountry(parsed.country); setRaw(parsed.formatNational()); }
  }, [value]);

  const handleChange = (input: string) => {
    const formatter = new AsYouType(country);
    const formatted = formatter.input(input);
    setRaw(formatted);
    const parsed = parsePhoneNumberFromString(input, country);
    onChange({
      e164: parsed?.isValid() ? parsed.format('E.164') : '',
      country: parsed?.country ?? country,
      isValid: parsed?.isValid() ?? false,
    });
  };

  const handleCountryChange = (c: CountryCode) => {
    setCountry(c);
    handleChange(raw);
  };

  return (
    <div className={clsx('flex gap-2', className)}>
      <CountrySelect value={country} onChange={handleCountryChange} />
      <input value={raw} onChange={(e) => handleChange(e.target.value)} placeholder={placeholder ?? 'Phone'}
        className="flex-1 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary" />
    </div>
  );
};
